// FE#332 — the status-aware "finish" behaviour of the create/edit wizard.
//
// EDIT reuses the CREATE wizard (same UI, prepopulated) and saves back to the
// SAME drip. The only thing that differs is what the finish button DOES:
//
//   • a DRAFT (never launched)  → LAUNCH  (mint step-0 + start sending)
//   • an already-LAUNCHED drip  → SAVE IN PLACE (patch the drip document only)
//
// The save-in-place path must NEVER call `launchDrip`/`advanceDrip`: each of
// those mints a fresh step-0 campaign over the whole audience, so calling one on
// a live drip messages every recipient twice. Patching the drip instead is
// double-send-safe BY CONSTRUCTION — the backend `syncPendingStepCampaignOnPatch`
// hook fans `steps`+`boardIDArray` onto the still-PENDING step campaigns and is
// guarded to skip any step that is already running/complete (frozen into Redis
// at run-start). So a running step is never mutated regardless of what the FE
// sends; the FE read-only gate below is honest UX, not the safety mechanism.
import type { Drip, DripStep } from '@bt-common/schemas/bt/drip';
import { dripHasLaunched } from './campaign-editability';

export type WizardCompletionMode = 'launch' | 'save-edits';

/** What the wizard's finish button does for this drip. `save-edits` iff the drip
 *  has already launched (see `dripHasLaunched` — status past `draft`, or a
 *  server-stamped `startedAt` that survives the draft-drift window). */
export const resolveWizardCompletionMode = (
  dripStatus?: string | null,
  startedAt?: unknown,
): WizardCompletionMode =>
  dripHasLaunched(dripStatus, startedAt) ? 'save-edits' : 'launch';

/** The patch for an edit-in-place save of a launched drip. Carries the full
 *  authored content (name/audience/steps/gaps/schedule/stopOnReply) but
 *  DELIBERATELY omits `status` and `currentStep`: a save must never re-draft a
 *  live drip (the old bug: `handleLaunch` wrote `status:'draft'` then relaunched)
 *  nor rewind its progress. The result is a pure content update the orchestrator
 *  propagates only to pending steps. */
export const buildDripEditPatch = (args: {
  dripId: string;
  name: string;
  boardIDArray: (string | number)[];
  steps: DripStep[];
  stopOnReply: boolean;
  schedule: unknown;
  /** null clears a previously-set randomized gap (undefined would be dropped). */
  randomGap?: Drip['randomGap'];
}): Partial<Drip> & { _id: string } => ({
  _id: args.dripId,
  name: args.name,
  boardIDArray: args.boardIDArray,
  steps: args.steps,
  stopOnReply: args.stopOnReply,
  schedule: args.schedule as Drip['schedule'],
  ...(args.randomGap !== undefined ? { randomGap: args.randomGap } : {}),
});

/** Is the drip ACTIVELY dispatching its current step right now (as opposed to a
 *  scheduled/pending step still in the future)? A running step's audience + send
 *  time are frozen into Redis at run-start, so the wizard shows THOSE controls
 *  read-only with an "already sending" note. A pending/absent status is NOT
 *  actively sending → fully editable (the common "wrong group on a scheduled
 *  send" case the regression broke). */
export const isDripActivelySending = (
  currentStepCampaignStatus?: string | null,
): boolean => currentStepCampaignStatus === 'running';
